import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DivineBackdrop, OrnateGoldBadge, GoldFooterPill, BODY_FONT_STYLE } from './shared';

const PreServiceDisplay = ({ content }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const audioRef = useRef(null);
  const announcements = content?.announcements || ['Welcome to our service'];
  const musicUrl = content?.music_url;
  const interval = (content?.rotation_interval || 10) * 1000;

  useEffect(() => {
    if (musicUrl && audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, [musicUrl]);

  useEffect(() => {
    if (announcements.length <= 1) return;
    const timer = setInterval(() => setCurrentIndex(prev => (prev + 1) % announcements.length), interval);
    return () => clearInterval(timer);
  }, [announcements.length, interval]);

  return (
    <div className="w-full h-full relative overflow-hidden" data-testid="pre-service-display" style={{ height: '100vh' }}>
      <DivineBackdrop />
      {musicUrl && <audio ref={audioRef} src={musicUrl} loop />}

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-12">
        <motion.div initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
          <OrnateGoldBadge>PRE-SERVICE</OrnateGoldBadge>
        </motion.div>

        <div style={{ marginTop: '4vh', width: '100%', maxWidth: '85vw' }}>
          <AnimatePresence mode="wait">
            <motion.p
              key={currentIndex}
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -24, filter: 'blur(8px)' }}
              transition={{ duration: 0.55 }}
              className="text-white"
              style={{
                ...BODY_FONT_STYLE,
                fontWeight: 900,
                fontSize: 'clamp(32px, 6.5vh, 92px)',
                lineHeight: 1.18,
                margin: 0,
              }}
            >
              {announcements[currentIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.55 }}
          style={{ marginTop: '6vh' }}
        >
          <GoldFooterPill>SERVICE BEGINS SHORTLY</GoldFooterPill>
        </motion.div>

        {announcements.length > 1 && (
          <div className="absolute bottom-10 flex gap-3" data-testid="pre-service-dots">
            {announcements.map((_, idx) => (
              <div
                key={idx}
                className="rounded-full transition-all"
                style={{
                  width: idx === currentIndex ? '14px' : '8px',
                  height: '8px',
                  backgroundColor: idx === currentIndex ? '#fde68a' : 'rgba(255,255,255,0.35)',
                  boxShadow: idx === currentIndex ? '0 0 10px rgba(251,191,36,0.6)' : 'none',
                }}
              />
            ))}
          </div>
        )}

        {musicUrl && (
          <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(16,32,74,0.6)', border: '1px solid rgba(251,191,36,0.35)' }}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#86efac' }} />
            <span className="text-xs text-white/70 font-bold tracking-wider">MUSIC PLAYING</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreServiceDisplay;
